import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, ShieldAlert, Users as UsersIcon, FileText, Handshake,
  Search, RefreshCw, Trash2, Pencil, KeyRound, CheckCircle2, X, Copy, ExternalLink,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { useSignInDialog } from '../context/SignInDialogContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

const roleBadgeClasses = (role) => ({
  superadmin: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  employee:   'bg-blue-500/15 text-blue-300 border-blue-500/40',
  partner:    'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  visitor:    'bg-neutral-700/30 text-neutral-300 border-neutral-700',
}[role] || 'bg-neutral-700/30 text-neutral-300 border-neutral-700');

const statusBadgeClasses = (s) => ({
  pending_approval: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  approved:         'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  rejected:         'bg-red-500/15 text-red-300 border-red-500/40',
  new:              'bg-blue-500/15 text-blue-300 border-blue-500/40',
}[s] || 'bg-neutral-700/30 text-neutral-300 border-neutral-700');

// ----- Users tab -----
const UsersPanel = ({ currentAdminId }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [total, setTotal] = useState(0);

  const [editUser, setEditUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [resetResult, setResetResult] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/admin/users`, {
        withCredentials: true,
        params: { q: q || undefined, role: roleFilter === 'all' ? undefined : roleFilter, page: 1, page_size: 100 },
      });
      setUsers(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [q, roleFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mb-5">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-neutral-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or email…"
              className="pl-9 bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 focus-visible:ring-blue-500 focus-visible:border-blue-500 h-10"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-40 bg-neutral-900 border-neutral-800 text-white h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-neutral-950 border-neutral-800 text-white">
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="superadmin">Super admin</SelectItem>
              <SelectItem value="employee">Employee</SelectItem>
              <SelectItem value="partner">Partner</SelectItem>
              <SelectItem value="visitor">Visitor</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 text-[13px] text-neutral-400">
          <span>{total} {total === 1 ? 'user' : 'users'}</span>
          <Button onClick={fetchUsers} size="sm" variant="outline" className="border-neutral-800 bg-transparent text-white hover:bg-neutral-900">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : error ? (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">{error}</div>
      ) : users.length === 0 ? (
        <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-10 text-center text-neutral-500">No users found.</div>
      ) : (
        <div className="rounded-2xl border border-neutral-800 bg-neutral-950 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13.5px]">
              <thead className="bg-neutral-900/60 text-neutral-400 text-[11.5px] uppercase tracking-wider">
                <tr>
                  <th className="px-5 py-3 font-medium">User</th>
                  <th className="px-5 py-3 font-medium">Role</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Created</th>
                  <th className="px-5 py-3 font-medium">Last login</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-900">
                {users.map((u) => (
                  <tr key={u.user_id} className="hover:bg-neutral-900/50">
                    <td className="px-5 py-3">
                      <div className="font-medium text-white">{u.name}</div>
                      <div className="text-[12.5px] text-neutral-400">{u.email}</div>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] capitalize ${roleBadgeClasses(u.role)}`}>{u.role}</span>
                    </td>
                    <td className="px-5 py-3">
                      {u.is_active === false ? (
                        <span className="text-red-300 text-[12.5px]">Disabled</span>
                      ) : (
                        <span className="text-emerald-300 text-[12.5px]">Active</span>
                      )}
                      {u.is_test_user && <span className="ml-2 text-[10.5px] text-neutral-500 uppercase tracking-wider">test</span>}
                    </td>
                    <td className="px-5 py-3 text-neutral-300">{fmtDate(u.created_at)}</td>
                    <td className="px-5 py-3 text-neutral-300">{fmtDate(u.last_login_at)}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex gap-1.5">
                        <button title="Edit" onClick={() => setEditUser(u)} className="p-1.5 rounded-md hover:bg-neutral-800 text-neutral-300 hover:text-white">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button title="Reset password" onClick={() => { setResetUser(u); setResetResult(null); }} className="p-1.5 rounded-md hover:bg-neutral-800 text-neutral-300 hover:text-white">
                          <KeyRound className="w-3.5 h-3.5" />
                        </button>
                        <button
                          title="Delete"
                          disabled={u.user_id === currentAdminId}
                          onClick={() => setDeleteUser(u)}
                          className="p-1.5 rounded-md hover:bg-red-500/10 text-red-300 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <EditUserDialog user={editUser} onClose={() => setEditUser(null)} onSaved={fetchUsers} />
      <DeleteUserDialog user={deleteUser} onClose={() => setDeleteUser(null)} onDeleted={fetchUsers} />
      <ResetPasswordDialog user={resetUser} result={resetResult} setResult={setResetResult} onClose={() => { setResetUser(null); setResetResult(null); }} />
    </div>
  );
};

const EditUserDialog = ({ user, onClose, onSaved }) => {
  const open = !!user;
  const [name, setName] = useState('');
  const [role, setRole] = useState('partner');
  const [isActive, setIsActive] = useState(true);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      setName(user.name || ''); setRole(user.role || 'partner'); setIsActive(user.is_active !== false);
      setStatus('idle'); setError('');
    }
  }, [user]);

  const save = async (e) => {
    e.preventDefault(); setStatus('loading'); setError('');
    try {
      await axios.patch(`${API}/admin/users/${user.user_id}`, { name, role, is_active: isActive }, { withCredentials: true });
      onSaved(); onClose();
    } catch (err) {
      setStatus('error'); setError(err?.response?.data?.detail || 'Failed to save changes');
    }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-neutral-950 border border-neutral-800 text-white sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Edit user</DialogTitle>
          <DialogDescription className="text-neutral-400">{user.email}</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label htmlFor="eu-name" className="text-[13px] text-neutral-300">Name</Label>
            <Input id="eu-name" value={name} onChange={(e) => setName(e.target.value)} className="bg-neutral-900 border-neutral-800 text-white h-10" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[13px] text-neutral-300">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="bg-neutral-900 border-neutral-800 text-white h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-neutral-950 border-neutral-800 text-white">
                <SelectItem value="superadmin">Super admin</SelectItem>
                <SelectItem value="employee">Employee</SelectItem>
                <SelectItem value="partner">Partner</SelectItem>
                <SelectItem value="visitor">Visitor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-[13.5px] text-neutral-300 cursor-pointer">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="accent-blue-500 w-4 h-4" />
            Account active (can sign in)
          </label>
          {error && <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-red-300 text-[13px]">{error}</div>}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-neutral-700 bg-transparent text-white hover:bg-neutral-900">Cancel</Button>
            <Button type="submit" disabled={status === 'loading'} className="bg-white text-neutral-950 hover:bg-neutral-200">
              {status === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const DeleteUserDialog = ({ user, onClose, onDeleted }) => {
  const open = !!user;
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  useEffect(() => { setStatus('idle'); setError(''); }, [user]);

  const remove = async () => {
    setStatus('loading'); setError('');
    try {
      await axios.delete(`${API}/admin/users/${user.user_id}`, { withCredentials: true });
      onDeleted(); onClose();
    } catch (err) {
      setStatus('error'); setError(err?.response?.data?.detail || 'Failed to delete user');
    }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-neutral-950 border border-neutral-800 text-white sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Delete user?</DialogTitle>
          <DialogDescription className="text-neutral-400">
            This permanently removes <span className="text-white">{user.email}</span> from MetaxuOne, including their sessions. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        {error && <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-red-300 text-[13px]">{error}</div>}
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onClose} className="border-neutral-700 bg-transparent text-white hover:bg-neutral-900">Cancel</Button>
          <Button onClick={remove} disabled={status === 'loading'} className="bg-red-500 hover:bg-red-600 text-white">
            {status === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete user'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ResetPasswordDialog = ({ user, result, setResult, onClose }) => {
  const open = !!user;
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => { setStatus('idle'); setError(''); setCopied(false); }, [user]);

  const trigger = async () => {
    setStatus('loading'); setError('');
    try {
      const { data } = await axios.post(`${API}/admin/users/${user.user_id}/reset-password`, {}, { withCredentials: true });
      setResult(data); setStatus('done');
    } catch (err) {
      setStatus('error'); setError(err?.response?.data?.detail || 'Failed to start reset');
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(result.reset_link); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* noop */ }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-neutral-950 border border-neutral-800 text-white sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Reset password</DialogTitle>
          <DialogDescription className="text-neutral-400">
            Generate a single-use reset link for <span className="text-white">{user.email}</span>.
          </DialogDescription>
        </DialogHeader>
        {!result ? (
          <>
            <p className="text-[13.5px] text-neutral-400 leading-relaxed">
              A reset link will be generated (and emailed when possible) — valid for 60 minutes. Any existing sessions for this user will be revoked when they set a new password.
            </p>
            {error && <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-red-300 text-[13px]">{error}</div>}
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={onClose} className="border-neutral-700 bg-transparent text-white hover:bg-neutral-900">Cancel</Button>
              <Button onClick={trigger} disabled={status === 'loading'} className="bg-white text-neutral-950 hover:bg-neutral-200">
                {status === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate reset link'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="rounded-md border border-blue-500/30 bg-blue-500/10 p-3 text-blue-200 text-[13px]">
              {result.delivered_via_email
                ? 'Reset email dispatched. You can also share the link directly:'
                : 'Email delivery failed — share this link with the user directly:'}
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2.5">
              <code className="flex-1 text-[12px] text-blue-300 break-all">{result.reset_link}</code>
              <button onClick={copyLink} className="text-neutral-400 hover:text-white" title="Copy"><Copy className="w-3.5 h-3.5" /></button>
              <a href={result.reset_link} target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-white" title="Open"><ExternalLink className="w-3.5 h-3.5" /></a>
            </div>
            {copied && <p className="text-[12px] text-blue-400">Link copied to clipboard.</p>}
            <p className="text-[12px] text-neutral-500">Expires at {fmtDate(result.expires_at)}.</p>
            <DialogFooter>
              <Button onClick={onClose} className="bg-white text-neutral-950 hover:bg-neutral-200">Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ----- Leads tab -----
const LeadsPanel = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchLeads = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/admin/leads`, { withCredentials: true });
      setLeads(data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load leads');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const remove = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}`, { withCredentials: true });
      fetchLeads();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <p className="text-[14px] text-neutral-400">{leads.length} contact form submissions</p>
        <Button onClick={fetchLeads} size="sm" variant="outline" className="border-neutral-800 bg-transparent text-white hover:bg-neutral-900">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>
      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : error ? (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">{error}</div>
      ) : leads.length === 0 ? (
        <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-10 text-center text-neutral-500">No leads yet.</div>
      ) : (
        <div className="rounded-2xl border border-neutral-800 bg-neutral-950 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13.5px]">
              <thead className="bg-neutral-900/60 text-neutral-400 text-[11.5px] uppercase tracking-wider">
                <tr>
                  <th className="px-5 py-3 font-medium">Contact</th>
                  <th className="px-5 py-3 font-medium">Company</th>
                  <th className="px-5 py-3 font-medium">Source</th>
                  <th className="px-5 py-3 font-medium">Submitted</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-900">
                {leads.map((l) => (
                  <tr key={l.id} className="hover:bg-neutral-900/50">
                    <td className="px-5 py-3">
                      <div className="font-medium text-white">{l.name}</div>
                      <a href={`mailto:${l.email}`} className="text-[12.5px] text-blue-400 hover:text-blue-300">{l.email}</a>
                    </td>
                    <td className="px-5 py-3 text-neutral-300">{l.company}</td>
                    <td className="px-5 py-3 text-neutral-400 text-[12.5px]">{l.source || '—'}</td>
                    <td className="px-5 py-3 text-neutral-300">{fmtDate(l.submitted_at)}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => remove(l.id)} className="p-1.5 rounded-md hover:bg-red-500/10 text-red-300" title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ----- Deals tab -----
const DealsPanel = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [decideOn, setDecideOn] = useState(null); // { deal, decision }
  const [decisionNote, setDecisionNote] = useState('');
  const [decideStatus, setDecideStatus] = useState('idle');

  const fetchDeals = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/admin/deals`, {
        withCredentials: true,
        params: { status: statusFilter === 'all' ? undefined : statusFilter },
      });
      setDeals(data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load deals');
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  const submitDecision = async () => {
    if (!decideOn) return;
    setDecideStatus('loading');
    try {
      await axios.patch(`${API}/admin/deals/${decideOn.deal.id}`, {
        status: decideOn.decision,
        decision_note: decisionNote || undefined,
      }, { withCredentials: true });
      setDecideOn(null); setDecisionNote(''); setDecideStatus('idle');
      fetchDeals();
    } catch (err) {
      setDecideStatus('error');
      alert(err?.response?.data?.detail || 'Failed to update');
    }
  };

  const removeDeal = async (id) => {
    if (!window.confirm('Delete this deal registration?')) return;
    try {
      await axios.delete(`${API}/admin/deals/${id}`, { withCredentials: true });
      fetchDeals();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  const counts = useMemo(() => {
    const c = { pending_approval: 0, approved: 0, rejected: 0 };
    deals.forEach((d) => { if (c[d.status] !== undefined) c[d.status] += 1; });
    return c;
  }, [deals]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mb-5">
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48 bg-neutral-900 border-neutral-800 text-white h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-neutral-950 border-neutral-800 text-white">
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending_approval">Pending approval</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-[12.5px] text-neutral-500">
            {counts.pending_approval} pending · {counts.approved} approved · {counts.rejected} rejected
          </span>
        </div>
        <Button onClick={fetchDeals} size="sm" variant="outline" className="border-neutral-800 bg-transparent text-white hover:bg-neutral-900">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : error ? (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">{error}</div>
      ) : deals.length === 0 ? (
        <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-10 text-center text-neutral-500">No deals to display.</div>
      ) : (
        <div className="rounded-2xl border border-neutral-800 bg-neutral-950 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[13.5px]">
              <thead className="bg-neutral-900/60 text-neutral-400 text-[11.5px] uppercase tracking-wider">
                <tr>
                  <th className="px-5 py-3 font-medium">Deal</th>
                  <th className="px-5 py-3 font-medium">Customer</th>
                  <th className="px-5 py-3 font-medium">Product · Qty</th>
                  <th className="px-5 py-3 font-medium">Partner</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-900">
                {deals.map((d) => (
                  <tr key={d.id} className="hover:bg-neutral-900/50 align-top">
                    <td className="px-5 py-3">
                      <code className="text-blue-400 text-[12px]">{d.deal_code}</code>
                      <div className="text-[11.5px] text-neutral-500 mt-1">{fmtDate(d.submitted_at)}</div>
                    </td>
                    <td className="px-5 py-3 text-white">{d.customer_name}</td>
                    <td className="px-5 py-3 text-neutral-300">{d.product} · <span className="text-neutral-400">{d.quantity}</span></td>
                    <td className="px-5 py-3">
                      <div className="text-white">{d.partner_company}</div>
                      <div className="text-[12.5px] text-neutral-400">{d.partner_person_name}</div>
                      {d.partner_email && <a href={`mailto:${d.partner_email}`} className="text-[12px] text-blue-400 hover:text-blue-300">{d.partner_email}</a>}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] capitalize ${statusBadgeClasses(d.status)}`}>{(d.status || '').replace('_', ' ')}</span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex gap-1.5">
                        {d.status === 'pending_approval' && (
                          <>
                            <button onClick={() => { setDecideOn({ deal: d, decision: 'approved' }); setDecisionNote(''); }}
                              className="px-2 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[12px] hover:bg-emerald-500/25">
                              Approve
                            </button>
                            <button onClick={() => { setDecideOn({ deal: d, decision: 'rejected' }); setDecisionNote(''); }}
                              className="px-2 py-1 rounded-md bg-red-500/10 border border-red-500/40 text-red-300 text-[12px] hover:bg-red-500/20">
                              Reject
                            </button>
                          </>
                        )}
                        <button onClick={() => removeDeal(d.id)} className="p-1.5 rounded-md hover:bg-red-500/10 text-red-300" title="Delete">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!decideOn} onOpenChange={(v) => { if (!v) { setDecideOn(null); setDecisionNote(''); } }}>
        <DialogContent className="bg-neutral-950 border border-neutral-800 text-white sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>{decideOn?.decision === 'approved' ? 'Approve deal' : 'Reject deal'}</DialogTitle>
            <DialogDescription className="text-neutral-400">
              {decideOn ? `${decideOn.deal.deal_code} · ${decideOn.deal.partner_company} → ${decideOn.deal.customer_name}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="dn" className="text-[13px] text-neutral-300">Decision note (optional)</Label>
            <Textarea id="dn" value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} rows={3}
              className="bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 focus-visible:ring-blue-500 focus-visible:border-blue-500"
              placeholder="Internal note — shown only to admins." />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setDecideOn(null)} className="border-neutral-700 bg-transparent text-white hover:bg-neutral-900">Cancel</Button>
            <Button onClick={submitDecision} disabled={decideStatus === 'loading'}
              className={decideOn?.decision === 'approved'
                ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                : 'bg-red-500 hover:bg-red-600 text-white'}>
              {decideStatus === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : (decideOn?.decision === 'approved' ? 'Approve' : 'Reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ----- Page shell -----
const AdminPage = () => {
  const { user, loading } = useAuth();
  const { open: openSignIn } = useSignInDialog();
  const navigate = useNavigate();
  const [tab, setTab] = useState('users');

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-7 h-7 text-blue-400 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="bg-black min-h-screen">
        <Navbar />
        <main className="max-w-[760px] mx-auto px-6 py-24 text-center">
          <div className="w-14 h-14 rounded-full bg-blue-500/15 border border-blue-500/40 mx-auto flex items-center justify-center">
            <ShieldAlert className="w-7 h-7 text-blue-400" />
          </div>
          <h1 className="mt-6 text-[32px] sm:text-[40px] leading-tight font-semibold text-white tracking-[-0.02em]">
            Super-admin access required
          </h1>
          <p className="mt-4 text-[15px] text-neutral-400 max-w-md mx-auto">Sign in with a super-admin account to manage users and submissions.</p>
          <Button onClick={() => openSignIn('signin')} className="mt-7 h-12 px-7 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium">
            Sign in
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  if (user.role !== 'superadmin') {
    return (
      <div className="bg-black min-h-screen">
        <Navbar />
        <main className="max-w-[760px] mx-auto px-6 py-24 text-center">
          <div className="w-14 h-14 rounded-full bg-amber-500/15 border border-amber-500/40 mx-auto flex items-center justify-center">
            <ShieldAlert className="w-7 h-7 text-amber-400" />
          </div>
          <h1 className="mt-6 text-[32px] sm:text-[40px] leading-tight font-semibold text-white tracking-[-0.02em]">Access restricted</h1>
          <p className="mt-4 text-[15px] text-neutral-400 max-w-md mx-auto">
            This area is for super-administrators only. You are signed in as <span className="text-white">{user.email}</span>.
          </p>
          <Button onClick={() => navigate('/')} className="mt-7 h-12 px-7 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium">
            Back to home
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-black min-h-screen">
      <Navbar />
      <main>
        <section className="border-b border-neutral-900 bg-black">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-12 pb-8">
            <p className="text-[13px] uppercase tracking-[0.18em] text-blue-400">Super Admin</p>
            <h1 className="mt-3 text-[36px] sm:text-[48px] leading-tight tracking-[-0.02em] font-semibold text-white">
              Admin Console
            </h1>
            <p className="mt-3 text-[15px] text-neutral-400">Manage users, leads, and deal registrations for MetaxuOne.</p>
          </div>
        </section>

        <section className="bg-black">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="bg-neutral-900 border border-neutral-800 p-1 h-11">
                <TabsTrigger value="users" className="data-[state=active]:bg-neutral-950 data-[state=active]:text-white text-neutral-400 text-[13px] font-medium gap-2">
                  <UsersIcon className="w-3.5 h-3.5" /> Users
                </TabsTrigger>
                <TabsTrigger value="leads" className="data-[state=active]:bg-neutral-950 data-[state=active]:text-white text-neutral-400 text-[13px] font-medium gap-2">
                  <FileText className="w-3.5 h-3.5" /> Leads
                </TabsTrigger>
                <TabsTrigger value="deals" className="data-[state=active]:bg-neutral-950 data-[state=active]:text-white text-neutral-400 text-[13px] font-medium gap-2">
                  <Handshake className="w-3.5 h-3.5" /> Deal Registrations
                </TabsTrigger>
              </TabsList>
              <TabsContent value="users" className="mt-6">
                <UsersPanel currentAdminId={user.user_id} />
              </TabsContent>
              <TabsContent value="leads" className="mt-6">
                <LeadsPanel />
              </TabsContent>
              <TabsContent value="deals" className="mt-6">
                <DealsPanel />
              </TabsContent>
            </Tabs>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AdminPage;
